import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import { useShippingSettings } from '../hooks/useShippingSettings';
import { getZoneSettings, SHIPPING_ZONE_KEYS, ZONE_LABELS } from '../utils/shippingZones';
import { prodottiAPI } from '../config/api';
import './Carrello.css';

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/150x150/2C5F2D/FFFFFF?text=Prodotto';

const resolveImageUrl = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const isAbsolute = trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:');
  const isRelative = trimmed.startsWith('/') || trimmed.startsWith('./');

  return isAbsolute || isRelative ? trimmed : null;
};

const getItemImage = (item) => {
  const sources = [
    item?.mainImageUrl,
    item?.images?.find((img) => resolveImageUrl(img)),
    item?.foto,
  ];

  for (const source of sources) {
    const resolved = resolveImageUrl(source);
    if (resolved) {
      return resolved;
    }
  }

  return PLACEHOLDER_IMAGE;
};

const Carrello = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getSubtotal, clearCart } = useCart();
  const { t } = useTranslation();
  const { settings: shippingSettings, loading: shippingLoading, error: shippingError } = useShippingSettings();
  const [estimatedZone, setEstimatedZone] = useState('italy');
  const [productsStock, setProductsStock] = useState({}); // { productId: stock }

  // Recupera lo stock aggiornato per tutti i prodotti nel carrello
  useEffect(() => {
    const fetchStocks = async () => {
      if (cart.length === 0) return;
      
      const stockPromises = cart.map(async (item) => {
        try {
          const product = await prodottiAPI.getById(item.id);
          return {
            id: item.id,
            stock: typeof product.quantita === 'number' ? product.quantita : (product.stock ?? null)
          };
        } catch (error) {
          console.warn(`Errore recupero stock per prodotto ${item.id}:`, error);
          return {
            id: item.id,
            stock: item.stock ?? null // Usa lo stock salvato come fallback
          };
        }
      });
      
      const stocks = await Promise.all(stockPromises);
      const stockMap = {};
      stocks.forEach(({ id, stock }) => {
        stockMap[id] = stock;
      });
      setProductsStock(stockMap);
    };
    
    fetchStocks();
  }, [cart]);

  const subtotal = getSubtotal();
  const zoneConfig = getZoneSettings(shippingSettings, estimatedZone);
  const freeShippingMin = parseFloat(zoneConfig.free_shipping_min) || 0;
  const shippingCostValue = parseFloat(zoneConfig.shipping_cost) || 0;
  const fixedShippingEnabled = shippingSettings?.fixed_shipping_enabled ?? false;
  
  // Se fixedShippingEnabled è true, applica sempre il costo fisso
  // Altrimenti, se freeShippingMin è 0, la spedizione è sempre gratuita
  // Altrimenti, è gratuita solo se il subtotale è >= al minimo
  const qualifiesForFree = fixedShippingEnabled 
    ? false 
    : (freeShippingMin === 0 || subtotal >= freeShippingMin);
  const appliedShippingCost = shippingError ? 0 : (qualifiesForFree ? 0 : shippingCostValue);
  const orderTotal = subtotal + appliedShippingCost;
  const missingAmount = Math.max(0, freeShippingMin - subtotal);
  const shippingStatusLabel = shippingLoading
    ? t('cart.shipping_loading')
    : shippingError
      ? t('cart.shipping_error')
      : `${qualifiesForFree ? t('cart.shipping_free') : `€ ${appliedShippingCost.toFixed(2)}`} (${zoneConfig.label})`;
  const handleZoneEstimateChange = (event) => {
    setEstimatedZone(event.target.value);
  };

  if (cart.length === 0) {
    return (
      <div className="carrello-page">
        <div className="container">
          <div className="carrello-empty">
            <div className="empty-icon">
              <i className="fas fa-shopping-cart"></i>
            </div>
            <h2>{t('cart.empty')}</h2>
            <p>{t('cart.empty_subtitle')}</p>
            <Link to="/" className="btn btn-primary">
              {t('cart.shop_now')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="carrello-page">
      <div className="container">
        <h1 className="carrello-title">
          <i className="fas fa-shopping-cart"></i> {t('cart.title')}
        </h1>

        <div className="carrello-layout">
          {/* Lista Prodotti */}
          <div className="carrello-items">
            {cart.map((item, index) => {
              // Usa lo stock aggiornato dal backend se disponibile, altrimenti quello salvato nel carrello
              const stockAvailable = productsStock[item.id] !== undefined 
                ? productsStock[item.id] 
                : (typeof item.stock === 'number' ? item.stock : null);
              const disableIncrease = stockAvailable !== null && item.quantita >= stockAvailable;
              const isSoldOut = stockAvailable !== null && stockAvailable <= 0;
              const remainingStock = stockAvailable !== null ? Math.max(0, stockAvailable - item.quantita) : null;
              return (
                <div key={item.id ?? `cart-item-${index}`} className="carrello-item">
                  <div className="item-image">
                    <img 
                      src={getItemImage(item)} 
                      alt={item.nome} 
                    />
                  </div>

                  <div className="item-info">
                    <Link to={`/prodotto/${item.id}`} className="item-name">
                      {item.nome}
                    </Link>
                    <p className="item-price">€ {parseFloat(item.prezzo).toFixed(2)}</p>
                    {stockAvailable !== null && (
                      <p className={`item-stock ${isSoldOut ? 'out' : ''}`}>
                        {isSoldOut
                          ? t('cart.item_out_of_stock')
                          : t('cart.item_stock_left', { values: { quantity: remainingStock } })}
                      </p>
                    )}
                  </div>

                  <div className="item-quantity">
                    <button 
                      onClick={() => updateQuantity(item.id, item.quantita - 1)}
                      className="quantity-btn"
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="quantity-value">{item.quantita}</span>
                    <button 
                      onClick={() => updateQuantity(item.id, item.quantita + 1)}
                      className="quantity-btn"
                      disabled={disableIncrease}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>

                  <button 
                    onClick={() => removeFromCart(item.id)}
                    className="btn-remove"
                    title="Rimuovi dal carrello"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Riepilogo Ordine */}
          <div className="carrello-summary">
            <h2>{t('cart.order_summary')}</h2>
            
            <div className="summary-row">
              <span>{t('cart.subtotal')} ({cart.length} {cart.length === 1 ? t('cart.item') : t('cart.items')})</span>
              <span>€ {subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>{t('cart.shipping')}</span>
              <span className={qualifiesForFree ? 'shipping-free' : ''}>{shippingStatusLabel}</span>
            </div>

            <div className="shipping-destination-selector">
              <label htmlFor="shipping-zone-select">{t('cart.shipping_destination')}</label>
              <select
                id="shipping-zone-select"
                value={estimatedZone}
                onChange={handleZoneEstimateChange}
                disabled={shippingLoading || shippingError}
              >
                {SHIPPING_ZONE_KEYS.map((zoneKey) => (
                  <option key={zoneKey} value={zoneKey}>
                    {ZONE_LABELS[zoneKey]}
                  </option>
                ))}
              </select>
              <p className="shipping-destination-hint">
                {t('cart.shipping_destination_hint')}
              </p>
            </div>

            {!shippingLoading && !shippingError && !fixedShippingEnabled && (freeShippingMin > 0 || shippingCostValue > 0) && (
              <div className={`shipping-banner ${qualifiesForFree ? 'success' : 'info'}`}>
                {qualifiesForFree
                  ? t('cart.free_shipping_unlocked')
                  : t('cart.free_shipping_missing', { values: { amount: missingAmount.toFixed(2) } })}
              </div>
            )}

            {shippingError && (
              <div className="shipping-banner warning">
                {t('cart.shipping_warning')}
              </div>
            )}

            <div className="summary-divider"></div>

            <div className="summary-total">
              <span>{t('cart.total')}</span>
              <span>€ {orderTotal.toFixed(2)}</span>
            </div>

            <button 
              className="btn btn-primary btn-checkout"
              onClick={() => navigate('/checkout')}
            >
              {t('cart.checkout')}
            </button>

            <button 
              className="btn btn-outline btn-continue"
              onClick={() => navigate('/')}
            >
              {t('cart.continue_shopping')}
            </button>

            <button 
              className="btn-clear-cart"
              onClick={clearCart}
            >
              {t('cart.clear_cart')}
            </button>
          </div>
        </div>
      </div>
      
      {/* Language Selector */}
      <LanguageSelector />
    </div>
  );
};

export default Carrello;

